import { useEffect, useRef } from 'react';
import { finalizeVoiceRecording, initVoiceRecording, type VoiceRecordingTrack } from '../../api';
import { useOptionalConciergeContext } from '../../ConciergeContextProvider';
import { getHeadersWithUniqueVisitorId } from '../../../Auth';

/**
 * `useVoiceRecorder` (Plan 3b) — dual-track audio capture for a voice call.
 *
 * Design (single PutObject per track on stop):
 *   - Auto-picks a codec via `MediaRecorder.isTypeSupported` (webm/opus on
 *     Chromium, mp4/aac on Safari) and stamps mime + extension on the
 *     manifest.
 *   - Calls `/concierge/voice/recording/init` once per call. The server
 *     mints two presigned PutObject URLs (one per track) up front.
 *   - Per track: starts a `MediaRecorder` WITHOUT a `timeslice` so all
 *     audio buffers internally and we get one `dataavailable` (the full
 *     blob) on `stop()`.
 *   - On call end: PUT each blob to its presigned URL (Content-Type bound
 *     to the mime the URL was minted with), then POST `/recording/finalize`
 *     with `{ track, durationMs, mimeType, byteSize, uploadOk }`.
 *
 * Why not multipart-during-call? S3 multipart parts (other than the last)
 * must be ≥ 5 MB; at 32 kbps mono Opus, every chunk is ~40 KB and
 * `completeMultipartUpload` always fails with "Your proposed upload is
 * smaller than the minimum allowed size." Multipart added complexity
 * without benefit at our bitrate. See the wiki entry for Plan 3b
 * verification round 2 for the failure-mode trace.
 *
 * Memory ceiling: the studio config caps calls at 10 min. At 32 kbps
 * mono Opus that's ~2.4 MB per track — comfortably in browser memory.
 */
export interface UseVoiceRecorderParams {
  conversationId: string;
  /** User microphone stream (always available once the call is live). */
  userStream?: MediaStream | null;
  /** Assistant track wrapped as a single-track MediaStream — arrives via `pc.ontrack`. */
  assistantStream?: MediaStream | null;
  /** True while the call is live. False during teardown — flips the recorder into stop+finalize. */
  enabled: boolean;
  /** Wall-clock at which the call became live; drives `tCallMs`. */
  liveStartMs?: number;
}

const AUDIO_BITRATE = 32_000;

type Codec = { mime: string; ext: string };

/** Try the broadly supported codecs; the first hit is used. Order matters. */
function pickCodec(): Codec | null {
  if (typeof window === 'undefined') return null;
  const M = (window as any).MediaRecorder as typeof MediaRecorder | undefined;
  if (!M || typeof M.isTypeSupported !== 'function') return null;
  const candidates: Codec[] = [
    { mime: 'audio/webm;codecs=opus', ext: 'webm' },
    { mime: 'audio/webm', ext: 'webm' },
    { mime: 'audio/mp4;codecs=mp4a.40.2', ext: 'mp4' },
    { mime: 'audio/mp4', ext: 'mp4' },
    { mime: 'audio/ogg;codecs=opus', ext: 'ogg' },
  ];
  for (const c of candidates) {
    try {
      if (M.isTypeSupported(c.mime)) return c;
    } catch {
      // ignore; continue
    }
  }
  return null;
}

/** Per-track recorder state held across the call. */
interface TrackState {
  track: VoiceRecordingTrack;
  recorder: MediaRecorder;
  chunks: Blob[];
  /** Wall-clock when this recorder hit `start` — for `durationMs` on stop. */
  recorderStartMs: number;
  /** Mime type chosen for this track. */
  mimeType: string;
  /** Set true after stop runs to short-circuit duplicate stops. */
  stopped: boolean;
  /** Presigned PUT URL minted at `/recording/init`. */
  presignedUrl: string;
}

/** Init-call result kept across renders. */
interface InitState {
  initialized: boolean;
  enabled: boolean;
  inFlightInit?: Promise<void>;
  cancelled: boolean;
  codec: Codec | null;
  presignedUrlUser?: string;
  presignedUrlAssistant?: string;
}

export function useVoiceRecorder(params: UseVoiceRecorderParams): void {
  const { conversationId, userStream, assistantStream, enabled, liveStartMs } = params;
  void liveStartMs; // not used directly; kept in the signature for future per-chunk telemetry

  // Plan 7 Part E3 — recording init/finalize honor the host-provided
  // apiBaseUrl (see useVoiceSession); ref avoids effect-dependency churn.
  const conciergeCtx = useOptionalConciergeContext();
  const apiBaseUrlRef = useRef<string | undefined>(conciergeCtx?.apiBaseUrl);
  apiBaseUrlRef.current = conciergeCtx?.apiBaseUrl;

  const initStateRef = useRef<InitState | null>(null);
  const userTrackRef = useRef<TrackState | null>(null);
  const assistantTrackRef = useRef<TrackState | null>(null);

  // ── Init pass — run once when the user stream + enabled flag are ready. ─
  useEffect(() => {
    if (!enabled || !conversationId || !userStream) return;
    if (initStateRef.current) return;

    const codec = pickCodec();
    initStateRef.current = {
      initialized: false,
      enabled: false,
      cancelled: false,
      codec,
    };

    if (!codec) {
      // eslint-disable-next-line no-console
      console.warn('[voice-recorder] no MediaRecorder codec supported; recording disabled');
      return;
    }

    const initState = initStateRef.current;
    initState.inFlightInit = (async () => {
      const headersToInject = await getHeadersWithUniqueVisitorId().catch(() => ({}));
      const result = await initVoiceRecording({ apiBaseUrl: apiBaseUrlRef.current,
        conversationId,
        mimeUser: codec.mime,
        mimeAssistant: codec.mime,
        extUser: codec.ext,
        extAssistant: codec.ext,
        headersToInject,
      });
      if (initState.cancelled) return;
      initState.initialized = true;
      initState.enabled = !!result.enabled;
      initState.presignedUrlUser = result.presignedUrls?.user;
      initState.presignedUrlAssistant = result.presignedUrls?.assistant;
      if (initState.enabled && (!initState.presignedUrlUser || !initState.presignedUrlAssistant)) {
        // Server returned `enabled: true` but no URLs (idempotent re-init).
        // We can't proceed without URLs; treat as disabled.
        // eslint-disable-next-line no-console
        console.warn('[voice-recorder] init returned enabled without presigned URLs; recording disabled for this call');
        initState.enabled = false;
      }
    })().catch(() => {
      initState.initialized = true;
      initState.enabled = false;
    });

    return () => {
      initState.cancelled = true;
      void teardownTrack('user', conversationId, userTrackRef, apiBaseUrlRef.current);
      void teardownTrack('assistant', conversationId, assistantTrackRef, apiBaseUrlRef.current);
    };
    // We intentionally DON'T include `userStream` / `liveStartMs` in deps —
    // re-running this effect would re-init the upload with new presigned URLs
    // and orphan the first ones until lifecycle. See useVoiceSession comment
    // about strict-mode safety.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, conversationId]);

  // ── Track lifecycle: start each recorder when its stream + init are ready. ─
  useEffect(() => {
    if (!enabled) return;
    const initState = initStateRef.current;
    if (!initState) return;

    let cancelled = false;
    const tick = async () => {
      if (initState.inFlightInit) {
        try {
          await initState.inFlightInit;
        } catch {
          // ignore — init swallowed errors already
        }
      }
      if (cancelled) return;
      if (!initState.enabled || !initState.codec) return;

      if (userStream && !userTrackRef.current && initState.presignedUrlUser) {
        userTrackRef.current = startTrack({
          track: 'user',
          stream: userStream,
          codec: initState.codec,
          presignedUrl: initState.presignedUrlUser,
        });
      }
      if (assistantStream && !assistantTrackRef.current && initState.presignedUrlAssistant) {
        assistantTrackRef.current = startTrack({
          track: 'assistant',
          stream: assistantStream,
          codec: initState.codec,
          presignedUrl: initState.presignedUrlAssistant,
        });
      }
    };
    void tick();

    return () => {
      cancelled = true;
    };
  }, [enabled, conversationId, userStream, assistantStream]);
}

/** Build + start a `MediaRecorder` for one track. NO timeslice — single blob on stop. */
function startTrack({
  track,
  stream,
  codec,
  presignedUrl,
}: {
  track: VoiceRecordingTrack;
  stream: MediaStream;
  codec: Codec;
  presignedUrl: string;
}): TrackState | null {
  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream, {
      mimeType: codec.mime,
      audioBitsPerSecond: AUDIO_BITRATE,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[voice-recorder] MediaRecorder construction failed', { track, err });
    return null;
  }

  const state: TrackState = {
    track,
    recorder,
    chunks: [],
    recorderStartMs: Date.now(),
    mimeType: codec.mime,
    stopped: false,
    presignedUrl,
  };

  recorder.addEventListener('dataavailable', (ev: BlobEvent) => {
    if (ev.data && ev.data.size > 0) state.chunks.push(ev.data);
  });

  recorder.addEventListener('error', (ev: Event) => {
    // eslint-disable-next-line no-console
    console.warn('[voice-recorder] MediaRecorder error', { track, ev });
  });

  try {
    // No timeslice — `dataavailable` fires once when stop() is called with
    // the entire blob. This is the cheapest path that produces one big PUT.
    recorder.start();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[voice-recorder] MediaRecorder.start failed', { track, err });
    return null;
  }

  return state;
}

/** PUT the assembled blob to S3 then POST `/recording/finalize`. */
async function teardownTrack(
  track: VoiceRecordingTrack,
  conversationId: string,
  ref: React.MutableRefObject<TrackState | null>,
  apiBaseUrl?: string,
): Promise<void> {
  const state = ref.current;
  if (!state || state.stopped) return;
  state.stopped = true;
  ref.current = null;

  // Stop the recorder. The `dataavailable` for the full blob fires synchronously
  // before stop() resolves on most browsers; we still wait a microtask for safety.
  try {
    if (state.recorder.state !== 'inactive') {
      state.recorder.stop();
    }
  } catch {
    // ignore
  }
  await new Promise((r) => setTimeout(r, 0));

  if (state.chunks.length === 0) {
    // Recorder produced nothing (call ended before any audio was captured).
    // Tell the server so it can mark the row missing without polling S3.
    const headersToInject = await getHeadersWithUniqueVisitorId().catch(() => ({}));
    await finalizeVoiceRecording({
      apiBaseUrl,
      conversationId,
      track,
      durationMs: 0,
      mimeType: state.mimeType,
      byteSize: 0,
      uploadOk: false,
      headersToInject,
    });
    return;
  }

  const blob = new Blob(state.chunks, { type: state.mimeType });
  const durationMs = Math.max(0, Date.now() - state.recorderStartMs);

  let uploadOk = false;
  try {
    const response = await fetch(state.presignedUrl, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': state.mimeType },
    });
    uploadOk = response.ok;
    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.warn('[voice-recorder] PUT failed', { track, status: response.status });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[voice-recorder] PUT threw', { track, err });
  }

  const headersToInject = await getHeadersWithUniqueVisitorId().catch(() => ({}));
  await finalizeVoiceRecording({
    apiBaseUrl,
    conversationId,
    track,
    durationMs,
    mimeType: state.mimeType,
    byteSize: blob.size,
    uploadOk,
    headersToInject,
  });
}
